import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebaseConfig';
import { useTranslation, Trans } from 'react-i18next';
import Logo from './components/Logo';
import './LinkedInTheme.css';

function OrganizationOnboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    orgName: '',
    domain: '',
    name: '',
    role: '',
    phone: '',
    address: '',
    size: '',
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error(t('organizationOnboarding.loginError'));
      const token = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/register-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || t('organizationOnboarding.defaultError'));

      // On success, redirect to Invite Employees (page handles reloading/role check)
      // We might need to force a reload or wait for claims update, but typically
      // the app state update in App.js listener might catch it or a refresh is needed.
      // For now, simple navigation.
        alert(t('organizationOnboarding.successMessage'));
        window.location.href = "/invite-employees"; 

    } catch (err) {
      console.error("Registration error:", err);
      let errorMessage = err.message;
      if (errorMessage === 'Failed to fetch' || errorMessage.includes('ERR_CONNECTION_REFUSED')) {
          errorMessage = t('organizationOnboarding.networkError');
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="li-layout">
      <div className="li-container" style={{ maxWidth: '800px', display: 'block', margin: '0 auto' }}>
        
        <div className="li-card" style={{ marginTop: '24px' }}>
            <div className="li-card-header" style={{ borderBottom: '1px solid #e0e0e0', padding: '16px 24px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>{t('organizationOnboarding.title')}</h2>
            </div>
            <div className="li-card-body" style={{ padding: '24px' }}>
                <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.7)', marginBottom: '24px' }}>
                    <Trans i18nKey="organizationOnboarding.subtitle">
                        To enable <strong>Invite Employees</strong> and other HR features, we need a few details about your company.
                    </Trans>
                </p>

                {error && (
                    <div className="li-alert-error">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                            <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
                        </svg>
                        <span>{error}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="li-form-row">
                        <div className="li-form-stacked" style={{ flex: 1 }}>
                            <label>{t('organizationOnboarding.orgName')}</label>
                            <input type="text" name="orgName" value={formData.orgName} onChange={handleChange} className="li-input" required />
                        </div>
                        <div className="li-form-stacked" style={{ flex: 1 }}>
                            <label>{t('organizationOnboarding.domain')}</label>
                            <input type="text" name="domain" value={formData.domain} onChange={handleChange} className="li-input" placeholder={t('organizationOnboarding.domainPlaceholder')} required />
                        </div>
                    </div>

                    <div className="li-form-row">
                        <div className="li-form-stacked" style={{ flex: 1 }}>
                            <label>{t('organizationOnboarding.yourName')}</label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} className="li-input" required />
                        </div>
                        <div className="li-form-stacked" style={{ flex: 1 }}>
                            <label>{t('organizationOnboarding.jobTitle')}</label>
                            <input type="text" name="role" value={formData.role} onChange={handleChange} className="li-input" required />
                        </div>
                    </div>

                    <div className="li-form-row">
                        <div className="li-form-stacked" style={{ flex: 1 }}>
                            <label>{t('organizationOnboarding.phone')}</label>
                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className="li-input" required />
                        </div>
                        <div className="li-form-stacked" style={{ flex: 1 }}>
                            <label>{t('organizationOnboarding.companySize')}</label>
                            <select name="size" value={formData.size} onChange={handleChange} className="li-input" required>
                                <option value="">{t('organizationOnboarding.selectSize')}</option>
                                <option value="1-10">{t('organizationOnboarding.sizeOption1')}</option>
                                <option value="11-50">{t('organizationOnboarding.sizeOption2')}</option>
                                <option value="51-200">{t('organizationOnboarding.sizeOption3')}</option>
                                <option value="201-500">{t('organizationOnboarding.sizeOption4')}</option>
                                <option value="500+">{t('organizationOnboarding.sizeOption5')}</option>
                            </select>
                        </div>
                    </div>

                    <div className="li-form-stacked">
                        <label>{t('organizationOnboarding.address')}</label>
                        <input type="text" name="address" value={formData.address} onChange={handleChange} className="li-input" required />
                    </div>

                    <div className="li-form-stacked">
                        <label>{t('organizationOnboarding.reasonLabel')}</label>
                        <textarea name="reason" value={formData.reason} onChange={handleChange} className="li-input" rows="3" required />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                        <button type="button" onClick={() => navigate('/welcome')} className="li-btn-outline-secondary" style={{ marginRight: '12px' }}>{t('organizationOnboarding.cancel')}</button>
                        <button type="submit" className="li-btn-primary" disabled={loading}>
                            {loading ? t('organizationOnboarding.registering') : t('organizationOnboarding.submit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>

      </div>
    </div>
  );
}

export default OrganizationOnboarding;
